// Not typechecking just due to the imported "parse" function.
import * as _ from "underscore";

export const MINUTE = 60 * 1000;
export const HOUR = 60 * MINUTE;
export const DAY = 24 * HOUR;
export const YEAR = 356.25 * DAY;

/* eslint-disable */
// code from https://github.com/csnover/js-iso8601/blob/master/iso8601.js
// MIT licence
export const parse = (date) => {
  const origParse = Date.parse;
  const numericKeys = [1, 4, 5, 6, 7, 10, 11];
  let timestamp;
  /** @type {any[]} */
  let struct;
  let minutesOffset = 0;

  // ES5 §15.9.4.2 states that the string should attempt to be parsed as a Date Time String Format string
  // before falling back to any implementation-specific date parsing, so that’s what we do, even if native
  // implementations could be faster
  //              1 YYYY                2 MM       3 DD           4 HH    5 mm       6 ss        7 msec        8 Z 9 ±    10 tzHH    11 tzmm
  if (
    (struct =
      /^(\d{4}|[+\-]\d{6})(?:-(\d{2})(?:-(\d{2}))?)?(?:T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{3}))?)?(?:(Z)|([+\-])(\d{2})(?::(\d{2}))?)?)?$/.exec(
        date
      ))
  ) {
    // avoid NaN timestamps caused by “undefined” values being passed to Date.UTC
    for (let i = 0, k; (k = numericKeys[i]); ++i) {
      struct[k] = +struct[k] || 0;
    }

    // allow undefined days and months
    struct[2] = (+struct[2] || 1) - 1;
    struct[3] = +struct[3] || 1;

    if (struct[8] !== "Z" && struct[9] !== undefined) {
      minutesOffset = struct[10] * 60 + struct[11];

      if (struct[9] === "+") {
        minutesOffset = 0 - minutesOffset;
      }
    }

    timestamp = Date.UTC(
      struct[1],
      struct[2],
      struct[3],
      struct[4],
      struct[5] + minutesOffset,
      struct[6],
      struct[7]
    );
  } else {
    timestamp = origParse ? origParse(date) : NaN;
  }

  return timestamp;
};
/* eslint-enable */

const formatItems = {
  YYYY: "getFullYear",
  MM: function (date) {
    let result = String(date.getMonth() + 1);
    if (result.length === 1) {
      result = "0" + result;
    }
    return result;
  },
  DD: function (date) {
    let result = String(date.getDate());
    if (result.length === 1) {
      result = "0" + result;
    }
    return result;
  },
};
export const format = (date, formatString) => {
  let result = formatString;
  _.each(formatItems, (func, name) => {
    const itemString = typeof func === "string" ? date[func]() : func(date);
    result = result.replace(new RegExp("%" + name, "g"), itemString);
  });
  return result;
};

export const objectIdFromDate = (date) =>
  Math.floor(date.getTime() / 1000).toString(16) + "0000000000000000";

export const dateFromObjectId = (objectId) => {
  let objectString;
  if (typeof objectId !== "string") {
    objectString = objectId.toString();
  } else {
    objectString = objectId;
  }
  return new Date(parseInt(objectString.substring(0, 8), 16) * 1000);
};

export const addDays = (date, days) => new Date(date.getTime() + days * DAY);

/**
 * Returns a new date, whose "UTC" values will be equivalent to local time at the given
 * timezoneOffset
 *
 * (THIS CAN'T BE USED FOR COMPARING WITH STORED DATE VALUES)
 */
export const localDateFromServerDate = (serverDate, timezoneOffset) =>
  new Date(serverDate.getTime() - timezoneOffset * MINUTE);

/**
 * Takes as @param localDate the date whose "UTC" values should be interpreted as the local time at
 * the given @param timezoneOffset, and returns a new date with the actual UTC time.
 */
export const serverDateFromLocalDate = (localDate, timezoneOffset) =>
  new Date(localDate.getTime() + timezoneOffset * MINUTE);

/** Remove all time info, snapping to the previous UTC midnight */
export const snapToDay = (date) =>
  new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
  );

/** Snap to the previous midnight at the given timezone */
export const snapToLocalDay = (date, timezoneOffset) => {
  const localDate = localDateFromServerDate(date, timezoneOffset);
  return serverDateFromLocalDate(snapToDay(localDate), timezoneOffset);
};

export const getLocalDateString = (date, timezoneOffset) => {
  const localDate = localDateFromServerDate(date, timezoneOffset);
  return format(localDate, "%YYYY-%MM-%DD");
};

const getUTCDateFromString = (dateString) => {
  const split = dateString.split("-");
  return new Date(Date.UTC(split[0], split[1] - 1, split[2]));
};

// Functions for handling Day type, which is the number of days since 1/1/1970 in the user's
// timezone.

/**
 * @param {Date} date
 * @param {number} timezoneOffset
 *
 * @returns {number} */
export const dateToDay = (date, timezoneOffset) => {
  /**
   * If you read the UTC value of targetDate, it will correspond to the local date in the given
   * timezoneOffset.
   */
  const targetDate = new Date(date.getTime() - timezoneOffset * MINUTE);
  return Math.floor(targetDate.getTime() / DAY);
};

/**
 * @param {number} day
 * @param {number} timezoneOffset
 *
 * @returns {Date} */
export const dayToDate = (day, timezoneOffset) =>
  new Date(day * DAY + timezoneOffset * MINUTE);

/**
 * @param {number} day
 *
 * @returns {string}
 */
export const serializeDay = (day) =>
  format(new Date(day * DAY), "%YYYY-%MM-%DD");

/**
 * @param {string} dayString
 *
 * @returns {number}
 */
export const parseDay = (dayString) => {
  return dateToDay(getUTCDateFromString(dayString), 0);
};

export const dayOfWeekString = (date) => {
  const daysOfWeek = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];

  return daysOfWeek[date.getDay()];
};

export const months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export const monthString = (date) => {
  return months[date.getMonth()];
};

/**
 * @param {number} time - milliseconds since epoch
 * @param {number} timezoneOffset - minutes
 */
export const getLocalHour = (time, timezoneOffset) =>
  new Date(time - timezoneOffset * 60 * 1000).getHours();

export const getTimezoneOffset = (date) =>
  (date ?? new Date()).getTimezoneOffset();
